<script setup lang="ts">
import { Check, ChevronsUpDown, Loader2, Search, X } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { apiApiReportsFieldOptionsPath } from '@/routes'

interface FieldOption {
  value: string
  label: string
  sublabel: string | null
  inactive: boolean
}

const props = defineProps<{
  baseEntity: string
  field: string
  modelValue: string[]
  entityLabel: string
}>()

const emit = defineEmits<{ 'update:modelValue': [string[]] }>()

const open = ref(false)
const loading = ref(false)
const hasLoaded = ref(false)
const results = ref<FieldOption[]>([])
const query = ref('')

const resolved = ref<Map<string, FieldOption>>(new Map())
const unresolved = ref<Set<string>>(new Set())

let debounceTimer: ReturnType<typeof setTimeout>

async function fetchOptions(params: Record<string, string | string[]>): Promise<FieldOption[]> {
  const url = apiApiReportsFieldOptionsPath({
    base_entity: props.baseEntity,
    field: props.field,
    ...params,
  })
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) return []
  return (await res.json()).options ?? []
}

async function search(q: string) {
  loading.value = true
  try {
    results.value = await fetchOptions(q ? { q } : {})
    results.value.forEach((option) => resolved.value.set(option.value, option))
  } finally {
    loading.value = false
    hasLoaded.value = true
  }
}

async function resolveSelected(values: string[]) {
  const missing = values.filter((v) => !resolved.value.has(v) && !unresolved.value.has(v))
  if (missing.length === 0) return

  const options = await fetchOptions({ values: missing })
  options.forEach((option) => resolved.value.set(option.value, option))

  const found = new Set(options.map((o) => o.value))
  missing.filter((v) => !found.has(v)).forEach((v) => unresolved.value.add(v))
}

watch(
  () => props.modelValue,
  (values) => resolveSelected(values),
  { immediate: true },
)

watch(open, (isOpen) => {
  if (!isOpen) return
  query.value = ''
  search('')
})

watch(query, (q) => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => search(q), 250)
})

const selected = computed<FieldOption[]>(() =>
  props.modelValue.map(
    (value) => resolved.value.get(value) ?? { value, label: value, sublabel: null, inactive: false },
  ),
)

function isSelected(value: string) {
  return props.modelValue.includes(value)
}

function toggle(option: FieldOption) {
  const next = isSelected(option.value)
    ? props.modelValue.filter((v) => v !== option.value)
    : [...props.modelValue, option.value]
  emit('update:modelValue', next)
}

function remove(value: string) {
  emit(
    'update:modelValue',
    props.modelValue.filter((v) => v !== value),
  )
}
</script>

<template>
  <div class="flex flex-wrap items-center gap-1.5">
    <Badge v-for="option in selected" :key="option.value" variant="secondary" class="gap-1 font-normal">
      <span :class="cn(unresolved.has(option.value) && 'text-muted-foreground italic')">{{ option.label }}</span>
      <span v-if="unresolved.has(option.value)" class="text-muted-foreground text-xs">(unknown)</span>
      <span v-else-if="option.inactive" class="text-muted-foreground text-xs">(inactive)</span>
      <button type="button" :aria-label="`Remove ${option.label}`" @click="remove(option.value)">
        <X class="size-3" />
      </button>
    </Badge>

    <Popover v-model:open="open">
      <PopoverTrigger as-child>
        <Button variant="outline" role="combobox" :aria-expanded="open" class="h-8 justify-between font-normal">
          <span class="text-muted-foreground">
            {{ selected.length ? `Add ${entityLabel.toLowerCase()}…` : `Select ${entityLabel.toLowerCase()}…` }}
          </span>
          <ChevronsUpDown class="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-72 p-0" align="start">
        <div class="border-border focus-within:border-ring flex items-center gap-2 border-b px-3 transition-colors">
          <Loader2 v-if="loading" class="text-muted-foreground size-4 shrink-0 animate-spin" />
          <Search v-else class="text-muted-foreground size-4 shrink-0" />
          <Input
            v-model="query"
            :placeholder="`Search ${entityLabel.toLowerCase()}…`"
            class="h-10 rounded-none border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <Command>
          <CommandList>
            <div v-if="!hasLoaded" class="flex h-52 items-center justify-center">
              <Loader2 class="text-muted-foreground size-5 animate-spin" />
            </div>
            <template v-else>
              <div v-if="results.length === 0" class="text-muted-foreground px-3 py-6 text-center text-sm">
                No {{ entityLabel.toLowerCase() }} found.
              </div>
              <CommandGroup v-else>
                <CommandItem
                  v-for="option in results"
                  :key="option.value"
                  :value="option.value"
                  class="items-start"
                  @select="toggle(option)"
                >
                  <Check
                    :class="cn('mt-0.5 size-4 shrink-0', isSelected(option.value) ? 'opacity-100' : 'opacity-0')"
                  />
                  <span class="flex min-w-0 flex-col">
                    <span class="truncate">
                      {{ option.label }}
                      <span v-if="option.inactive" class="text-muted-foreground text-xs">· Inactive</span>
                    </span>
                    <span v-if="option.sublabel" class="text-muted-foreground truncate text-xs">
                      {{ option.sublabel }}
                    </span>
                  </span>
                </CommandItem>
              </CommandGroup>
              <p class="text-muted-foreground border-t px-3 py-2 text-xs">
                Targeting many? Filter on {{ entityLabel }}: Type or {{ entityLabel }}: Active instead.
              </p>
            </template>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  </div>
</template>
